import { defer, firstValueFrom, Observable, of, Subject as RxjsSubject } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { NestedTreeControl } from '@angular/cdk/tree';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, viewChild, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { platform } from '../../../platform/platform';
import { ApplicationError } from '../../_errors/application-error';
import { Subject } from '../../_objects/subject';
import { RectInStructImage, SubjectLocation, SubjectModel, SubjectModelListItem, SubjectModelUpdate } from '../../_objects/subject-model';
import { FILE_SYSTEM } from '../../_offline/electron.constants';
import { UtilsService } from '../../_services/_utils.service';
import { CommonSnackBarService } from '../../_services/common-snack-bar.service';
import { FileSystemAccessService } from '../../_services/file-system-access.service';
import { LoginUserOnlineService } from '../../_services/http-api/login-user-online.service';
import { StructureService } from '../../_services/http-api/structure.service';
import { SubjectModelService } from '../../_services/http-api/subject-model.service';
import { SubjectService } from '../../_services/http-api/subject.service';
import { TenantConfigService } from '../../_services/http-api/tenant-config.service';
import { LangUpdateService } from '../../_services/lang-update.service';
import { NetworkStatusService } from '../../_services/network-status.service';
import { SpinnerService } from '../../_services/spinner.service';
import { SETTINGS_SUBJECT___QUERY_PARAMS, SettingSubjectQueryParam, StorageService } from '../../_services/storage.service';
import { CustomValidators } from '../../_shared/custom-validator';
import { COMPRESSED_FILETYPE, IMAGE_FILETYPE, PLATFORM, REGULAR_EXPRESSION, TenantConfig } from '../../app.constants';
import { CommonConfirmDialogComponent } from '../../common-dialog/common-confirm-dialog.component';
import { CommonDeleteDialogComponent } from '../../common-dialog/common-delete-dialog.component';
import { CommonErrorMessageDialogComponent } from '../../common-dialog/common-error-message-dialog.component';
import { CommonImportConfirmDialogComponent } from '../../common-dialog/common-import-confirm-dialog/common-import-confirm-dialog.component';
import { LocationAbbreviationEditDialogComponent } from './location-abbreviation-edit-dialog/location-abbreviation-edit-dialog.component';
import { SettingsSubjectEditDialogComponent } from './subject-edit-dialog/settings-subject-edit-dialog.component';
import { SubjectModelEditDialogComponent } from './subject-model-edit-dialog/subject-model-edit-dialog.component';
import { SubjectModelDbService } from "../../_services/database/subject-model-db.service";
import { FileManagerComponent, FileManagerConfig } from '../../common-dialog/file-manager/file-manager.component';
import { OperationMode, SelectionMode } from '../../common-dialog/file-manager/types';

export interface LocationWrapper {
  location: SubjectLocation;
  nest: number;
  hasChildren: boolean;
}

@Component({
    selector: 'app-settings-subject-location-edit-dialog',
    templateUrl: './settings-subject-location-edit-dialog.component.html',
    styleUrls: ['./settings-subject-location-edit-dialog.component.scss'],
    standalone: false
})
export class SettingsSubjectLocationEditDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  subjectModel: SubjectModel;
  subjectLocation: SubjectLocation;
  subjectLocationsExceptMine: SubjectLocation[];
  locationIndexList: number[];
  nameMaxLength = 50;
  regularExpression = this.utilsService.CombiningRegExp([
    '^',
    REGULAR_EXPRESSION.RESERVED_WORDS_PATTERN,
    '[',
    REGULAR_EXPRESSION.HALF_ALPHANUMERIC_PATTERN,
    REGULAR_EXPRESSION.SYMBOL_PATTERN,
    ']+$'
  ]);
  nameControl = new FormControl<string>('', [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.nameMaxLength),
    Validators.pattern(this.regularExpression)
  ]);
  abbreviationMaxLength = 50;
  abbreviationControl = new FormControl<string>('', [
    CustomValidators.requiredTrimmed(),
    Validators.maxLength(this.abbreviationMaxLength),
    Validators.pattern(this.regularExpression),
    this.duplicationValidator(),
    this.spaceValidator(),
    this.dotValidator()
  ]);
  inspectionForMaxLength = 100;
  topMaxLength = 3;
  topControl = new FormControl<string>(
    { value: '', disabled: !this.data.model || !this.data.model.structImagePath },
    [
      CustomValidators.requiredTrimmed(),
      Validators.maxLength(this.topMaxLength),
      Validators.pattern(REGULAR_EXPRESSION.NUMBER_PATTERN),
      this.overHundredTopValidator()
    ]);
  leftMaxLength = 3;
  leftControl = new FormControl<string>(
    { value: '', disabled: !this.data.model || !this.data.model.structImagePath },
    [
      CustomValidators.requiredTrimmed(), Validators.maxLength(this.leftMaxLength),
      Validators.pattern(REGULAR_EXPRESSION.NUMBER_PATTERN),
      this.overHundredLeftValidator()
    ]);
  widthMaxLength = 3;
  widthControl = new FormControl<string>(
    { value: '', disabled: !this.data.model || !this.data.model.structImagePath },
    [
      CustomValidators.requiredTrimmed(),
      Validators.maxLength(this.widthMaxLength),
      Validators.pattern(REGULAR_EXPRESSION.NUMBER_PATTERN),
      this.overHundredWidthValidator()
    ]);
  heightMaxLength = 3;
  heightControl = new FormControl<string>(
    { value: '', disabled: !this.data.model || !this.data.model.structImagePath },
    [
      CustomValidators.requiredTrimmed(),
      Validators.maxLength(this.heightMaxLength),
      Validators.pattern(REGULAR_EXPRESSION.NUMBER_PATTERN),
      this.overHundredHeightValidator()
    ]);
  identifierPrefix = '';
  inspectionForDataSource: MatTableDataSource<string> = new MatTableDataSource();
  isEdit = true;
  isFirstLocation = false;
  isTrans = false;
  ghostCanvas: HTMLCanvasElement;
  ghostCanvasCtx: CanvasRenderingContext2D;
  ghostCanvasWidth: number;
  ghostCanvasHeight: number;
  ghostPreviewLeftPx: number;
  ghostPreviewTopPx: number;
  ghostPreviewWidthPx: number;
  ghostPreviewHeightPx: number;
  onDrawingBind;
  initSubjectLocations: any;
  private onDestroy$ = new RxjsSubject();

  @ViewChild(MatSlideToggle) toggle: MatSlideToggle;
  @ViewChild('canvas') canvas: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<SubjectModelEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: CommonSnackBarService,
    private subjectModelService: SubjectModelService,
    private cd: ChangeDetectorRef,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private dialog: MatDialog,
    private utilsService: UtilsService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit() {
    this.isEdit = this.data.isEdit;
    this.subjectModel = this.data.model;
    this.subjectLocation = this.data.subjectLocation;
    this.locationIndexList = this.data.indexList;
    this.isFirstLocation = this.isEdit ? this.locationIndexList.length === 1 : this.locationIndexList.length === 0;
    this.initSubjectLocations = this.data.subjectLocation;
    this.setIdentifierData();
    this.onDrawingBind = this.onDrawing.bind(this);
  }

  ngAfterViewInit(): void {
    this.isEdit
      ? this.setEditLocationValue()
      : this.setNewLocationValue();
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  isDataPlateHidden(): boolean {
    return this.toggle?.checked
      || (this.subjectLocation?.isDataPlate && this.subjectModel?.hasInspections)
      || (this.subjectLocation?.isDataPlate && !this.isFirstLocation);
  }
  initDataPlate() {
    this.inspectionForDataSource.data = [];
    this.topControl.setValue('0');
    this.leftControl.setValue('0');
    this.widthControl.setValue('0');
    this.heightControl.setValue('0');
  }
  disabledDataPlateToggle(): boolean {
    let flg = false;
    if (this.isFirstLocation) {
      this.subjectModel.locations.forEach(location => {
        if (location.isDataPlate) {
          flg = true;
        }
      });
      if (this.isEdit) {
        if (this.subjectLocation.isDataPlate) {
          flg = false;
        }
      }
    }
    return flg;
  }
  setIdentifierData() {
    // Identifierを生成するために設定
    if (this.locationIndexList.length > 1 || (!this.isEdit && this.locationIndexList.length === 1)) {
      this.identifierPrefix += this.subjectModel.locations[this.locationIndexList[0]].abbreviation + '_';
    }
    if (this.locationIndexList.length > 2 || (!this.isEdit && this.locationIndexList.length === 2)) {
      this.identifierPrefix += this.subjectModel.locations[this.locationIndexList[0]]
        .children[this.locationIndexList[1]].abbreviation + '_';
    }
    // Identifierの重複確認をするために設定
    this.subjectLocationsExceptMine = JSON.parse(JSON.stringify(this.subjectModel.locations));
    if (this.isEdit) {
      if (this.locationIndexList.length === 1) {
        this.subjectLocationsExceptMine.splice(this.locationIndexList[0], 1);
      } else if (this.locationIndexList.length === 2) {
        this.subjectLocationsExceptMine[this.locationIndexList[0]].children.splice(this.locationIndexList[1], 1);
      } else if (this.locationIndexList.length === 3) {
        this.subjectLocationsExceptMine[this.locationIndexList[0]].children[this.locationIndexList[1]].
          children.splice(this.locationIndexList[2], 1);
      }
    }
  }
  setNewLocationValue() {
    this.toggle.checked = false; // 初期値:false
    this.inspectionForDataSource.data = []; // 初期値:[]
    if (this.isFirstLocation) {
      this.topControl.setValue('0');
      this.leftControl.setValue('0');
      this.widthControl.setValue('0');
      this.heightControl.setValue('0');
    } else {
      this.topControl.setValue(null);
      this.leftControl.setValue(null);
      this.widthControl.setValue(null);
      this.heightControl.setValue(null);
    }
  }
  setEditLocationValue() {
    this.nameControl.setValue(this.subjectLocation.name.trim());
    this.abbreviationControl.setValue(this.subjectLocation.abbreviation);
    this.toggle.checked = this.subjectLocation.isDataPlate;
    this.inspectionForDataSource.data = this.subjectLocation.inspectionFor.concat();
    this.topControl.setValue(this.subjectLocation.rectInStructImage.top !== null ?
      this.subjectLocation.rectInStructImage.top.toString() : null);
    this.leftControl.setValue(this.subjectLocation.rectInStructImage.left !== null ?
      this.subjectLocation.rectInStructImage.left.toString() : null);
    this.widthControl.setValue(this.subjectLocation.rectInStructImage.width !== null ?
      this.subjectLocation.rectInStructImage.width.toString() : null);
    this.heightControl.setValue(this.subjectLocation.rectInStructImage.height !== null ?
      this.subjectLocation.rectInStructImage.height.toString() : null);

  }
  updateSubjectModel() {
    this.oscSpinnerService.show(true);
    this.isTrans = true;
    const editModel: SubjectModelUpdate = new SubjectModelUpdate();
    editModel.name = this.subjectModel.name;
    editModel.locations = this.subjectModel.locations;
    editModel.schemaVersion = this.subjectModel.schemaVersion;
    editModel.id = this.subjectModel.id;
    editModel.isValid = this.subjectModel.isValid;
    editModel.inspectionType = this.subjectModel.inspectionType;

    this.subjectModelService.updateSubjectModel(editModel).subscribe(
      res => {
        this.subjectModel = res;
      },
      error => {
        this.oscSpinnerService.hide(true);
        this.isTrans = false;
        this.snackBar.open(this.translate.instant('settingsSubjectLocationEditDialog.updateSubjectModelError'), null, null, 5000);
        console.log(error);
      },
      () => {
        this.oscSpinnerService.hide(true);
        this.isTrans = false;
        this.snackBar.open(this.translate.instant('settingsSubjectLocationEditDialog.updateSubjectModelCompleted'), null, null, 5000);
        this.dialogRef.close(this.subjectModel);
      }
    );
  }
  addInspectionFor() {
    const tempInspectionFor = this.inspectionForDataSource.data;
    tempInspectionFor.push('');
    this.inspectionForDataSource.data = tempInspectionFor;
  }
  deleteInspectionFor(index: number) {
    const tempInspectionFor = this.inspectionForDataSource.data;
    tempInspectionFor.splice(index, 1);
    this.inspectionForDataSource.data = tempInspectionFor;
  }
  deleteLocation() {
    const tempSubjectModelLocations = this.subjectModel.locations;
    if (this.locationIndexList.length === 1) {
      tempSubjectModelLocations.splice(this.locationIndexList[0], 1);
    } else if (this.locationIndexList.length === 2) {
      tempSubjectModelLocations[this.locationIndexList[0]].children.splice(this.locationIndexList[1], 1);
    } else if (this.locationIndexList.length === 3) {
      tempSubjectModelLocations[this.locationIndexList[0]].children[this.locationIndexList[1]].
        children.splice(this.locationIndexList[2], 1);
    }
    this.validateIsUpdate(true);
  }
  changeStringCase(value: string): string {
    // lowerに統一してチェック
    return value.toLowerCase();
  }
  duplicationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      let isDuplication = false;
      this.subjectLocationsExceptMine.forEach(location => {
        if (this.isDuplicationIdentifier(location, this.identifierPrefix + this.changeStringCase(control.value))) {
          isDuplication = true;
        }
      });
      return isDuplication ? { 'duplication': 'false' } : null;
    };
  }

  // 空白文字(半角スペース/タブ/改行/改ページ)、全角スペースがあるか判定
  spaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const isSpace = REGULAR_EXPRESSION.SPACE_PATTERN.test(control.value);
      return isSpace ? { 'space': 'false' } : null;
    };
  }

  // ドット"."があるか判定
  dotValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const isDot = REGULAR_EXPRESSION.DOT_PATTERN.test(control.value);
      return isDot ? { 'dot': 'false' } : null;
    };
  }

  touchTopControlCheck() {
    this.topControl.updateValueAndValidity();
    this.topControl.markAsTouched();
  }
  touchHeightControlCheck() {
    this.heightControl.updateValueAndValidity();
    this.heightControl.markAsTouched();
  }
  touchLeftControlCheck() {
    this.leftControl.updateValueAndValidity();
    this.leftControl.markAsTouched();
  }
  touchWidthControlCheck() {
    this.widthControl.updateValueAndValidity();
    this.widthControl.markAsTouched();
  }
  overHundredTopValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return this.heightControl.value && parseInt(control.value, 10) + parseInt(this.heightControl.value, 10) > 100
        ? { 'over': 'false' } : null;
    };
  }
  overHundredLeftValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return this.widthControl.value && parseInt(control.value, 10) + parseInt(this.widthControl.value, 10) > 100
        ? { 'over': 'false' } : null;
    };
  }
  overHundredWidthValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return this.leftControl.value && parseInt(control.value, 10) + parseInt(this.leftControl.value, 10) > 100
        ? { 'over': 'false' } : null;
    };
  }
  overHundredHeightValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return this.topControl.value && parseInt(control.value, 10) + parseInt(this.topControl.value, 10) > 100
        ? { 'over': 'false' } : null;
    };
  }
  onBeginDraw(event: MouseEvent) {
    this.ghostCanvas = event.target as HTMLCanvasElement;
    this.ghostCanvasCtx = this.ghostCanvas.getContext('2d');
    this.ghostCanvasWidth = this.canvas.nativeElement.offsetWidth;
    this.ghostCanvasHeight = this.canvas.nativeElement.offsetHeight;
    this.ghostPreviewWidthPx = 0;
    this.ghostPreviewHeightPx = 0;
    this.ghostPreviewLeftPx = event.offsetX;
    this.ghostPreviewTopPx = event.offsetY;
    this.ghostCanvas.removeEventListener('mousemove', this.onDrawingBind);
    this.ghostCanvas.addEventListener('mousemove', this.onDrawingBind);
  }
  onDrawing(event: MouseEvent) {
    this.ghostCanvasCtx.strokeStyle = '#4894f8';
    this.ghostCanvasCtx.clearRect(0, 0, this.ghostCanvasWidth, this.ghostCanvasHeight);
    this.ghostPreviewWidthPx = event.offsetX - this.ghostPreviewLeftPx;
    this.ghostPreviewHeightPx = event.offsetY - this.ghostPreviewTopPx;
    this.ghostCanvasCtx.strokeRect(this.ghostPreviewLeftPx, this.ghostPreviewTopPx, this.ghostPreviewWidthPx, this.ghostPreviewHeightPx);
  }
  onEndDraw(event: MouseEvent, locStr: string) {
    // 一時的に100越えエラーを発生させないため初期化
    this.topControl.setValue('0');
    this.heightControl.setValue('0');
    this.leftControl.setValue('0');
    this.widthControl.setValue('0');
    // 矩形選択した範囲を設定
    if (this.ghostPreviewTopPx < event.offsetY) {
      this.topControl.setValue(Math.round(this.ghostPreviewTopPx * 100 / this.ghostCanvasHeight).toString());
      this.heightControl.setValue(Math.round((event.offsetY - this.ghostPreviewTopPx) * 100 / this.ghostCanvasHeight).toString());
    } else {
      this.topControl.setValue(Math.round(event.offsetY * 100 / this.ghostCanvasHeight).toString());
      this.heightControl.setValue(Math.round((this.ghostPreviewTopPx - event.offsetY) * 100 / this.ghostCanvasHeight).toString());
    }
    if (this.ghostPreviewLeftPx < event.offsetX) {
      this.leftControl.setValue(Math.round(this.ghostPreviewLeftPx * 100 / this.ghostCanvasWidth).toString());
      this.widthControl.setValue(Math.round((event.offsetX - this.ghostPreviewLeftPx) * 100 / this.ghostCanvasWidth).toString());
    } else {
      this.leftControl.setValue(Math.round(event.offsetX * 100 / this.ghostCanvasWidth).toString());
      this.widthControl.setValue(Math.round((this.ghostPreviewLeftPx - event.offsetX) * 100 / this.ghostCanvasWidth).toString());
    }
    this.ghostCanvasCtx.clearRect(0, 0, this.ghostCanvasWidth, this.ghostCanvasHeight);
    this.ghostCanvas.removeEventListener('mousemove', this.onDrawingBind);
  }
  isDuplicationIdentifier(targetLoc: SubjectLocation, termLocIdentifier: string): boolean {
    let isDuplication = false;
    if (this.changeStringCase(targetLoc.identifier) === this.changeStringCase(termLocIdentifier)) {
      isDuplication = true;
    }
    if (targetLoc.children && targetLoc.children.length > 0) {
      targetLoc.children.forEach(location => {
        if (this.isDuplicationIdentifier(location, this.changeStringCase(termLocIdentifier))) {
          isDuplication = true;
        }
      });
    }
    return isDuplication;
  }
  isRectInStructImageNoSetting(): boolean {
    return this.topControl.value == null || this.leftControl.value == null || this.widthControl.value == null ||
      this.heightControl.value == null || !REGULAR_EXPRESSION.NUMBER_PATTERN.test(this.topControl.value) ||
      !REGULAR_EXPRESSION.NUMBER_PATTERN.test(this.leftControl.value) || !REGULAR_EXPRESSION.NUMBER_PATTERN.test(this.widthControl.value) ||
      !REGULAR_EXPRESSION.NUMBER_PATTERN.test(this.heightControl.value) ||
      (this.topControl.value === '0' && this.leftControl.value === '0' && this.widthControl.value === '0' &&
        this.heightControl.value === '0') ||
      parseInt(this.topControl.value, 10) + parseInt(this.heightControl.value, 10) > 100 ||
      parseInt(this.leftControl.value, 10) + parseInt(this.widthControl.value, 10) > 100;
  }

  isLocationUpdate(): boolean {
    const data = this.initSubjectLocations;
    if (!data) {
      return true;
    } else {
      return data.abbreviation !== this.abbreviationControl.value
        || data.identifier !== this.identifierPrefix + this.abbreviationControl.value
        || data.isDataPlate !== this.toggle.checked;
    }
  }

  validateIsUpdate(isDelete: boolean = false) {
    if (this.subjectModel.hasInspections && (this.isLocationUpdate() || isDelete)) {
      const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
        width: '500px',
        height: '280px',
        data: {
          firstMessage: this.translate.instant('settingsSubjectLocationEditDialog.confirmText1'),
          secondMessage: this.translate.instant('settingsSubjectLocationEditDialog.confirmText2'),
          trueBtnText: this.translate.instant('commonConfirmDialog.ok'),
          falseBtnText: this.translate.instant('commonConfirmDialog.cancel'),
          dialogType: 'confirmation'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (isDelete) {
            this.updateSubjectModel();
          } else {
            this.submit();
          }
        }
      });
    } else {
      if (isDelete) {
        this.updateSubjectModel();
      } else {
        this.submit();
      }
    }
  }

  submit() {
    const tempSubjectModelLocations = this.subjectModel.locations;
    const tempSubjectLocation = new SubjectLocation();
    tempSubjectLocation.name = this.nameControl.value.trim();
    tempSubjectLocation.abbreviation = this.abbreviationControl.value;
    tempSubjectLocation.identifier = this.identifierPrefix + this.abbreviationControl.value;
    tempSubjectLocation.description = '';
    tempSubjectLocation.inspectionFor = this.inspectionForDataSource.data.filter(inspectionFor => inspectionFor);
    tempSubjectLocation.rectInStructImage = new RectInStructImage();
    tempSubjectLocation.rectInStructImage.top = Number(this.topControl.value);
    tempSubjectLocation.rectInStructImage.left = Number(this.leftControl.value);
    tempSubjectLocation.rectInStructImage.width = Number(this.widthControl.value);
    tempSubjectLocation.rectInStructImage.height = Number(this.heightControl.value);
    if (!this.isEdit) {
      tempSubjectLocation.children = [];
      if (this.locationIndexList.length === 0) {
        tempSubjectModelLocations.push(tempSubjectLocation);
        tempSubjectLocation.isDataPlate = this.toggle.checked;
      } else if (this.locationIndexList.length === 1) {
        tempSubjectLocation.isDataPlate = tempSubjectModelLocations[this.locationIndexList[0]].isDataPlate;
        tempSubjectModelLocations[this.locationIndexList[0]].children.push(tempSubjectLocation);
      } else if (this.locationIndexList.length === 2) {
        tempSubjectLocation.isDataPlate = tempSubjectModelLocations[this.locationIndexList[0]].isDataPlate;
        tempSubjectModelLocations[this.locationIndexList[0]].children[this.locationIndexList[1]].
          children.push(tempSubjectLocation);
      }
    } else {
      tempSubjectLocation.children = this.subjectLocation.children;
      tempSubjectLocation.isDataPlate = this.toggle.checked;
      if (this.locationIndexList.length === 1) {
        tempSubjectModelLocations[this.locationIndexList[0]] = tempSubjectLocation;
        tempSubjectModelLocations[this.locationIndexList[0]].children.forEach(firstLoc => {
          firstLoc.identifier = tempSubjectLocation.identifier + '_' + firstLoc.abbreviation;
          firstLoc.isDataPlate = this.toggle.checked;
          firstLoc.children.forEach(secondLoc => {
            secondLoc.identifier = firstLoc.identifier + '_' + secondLoc.abbreviation;
            secondLoc.isDataPlate = this.toggle.checked;
          });
        });
      } else if (this.locationIndexList.length === 2) {
        tempSubjectModelLocations[this.locationIndexList[0]].children[this.locationIndexList[1]] = tempSubjectLocation;
        tempSubjectModelLocations[this.locationIndexList[0]].children[this.locationIndexList[1]].children.forEach(secondLoc => {
          secondLoc.identifier = tempSubjectLocation.identifier + '_' + secondLoc.abbreviation;
          secondLoc.isDataPlate = this.toggle.checked;
        });
      } else if (this.locationIndexList.length === 3) {
        tempSubjectModelLocations[this.locationIndexList[0]].children[this.locationIndexList[1]]
          .children[this.locationIndexList[2]] = tempSubjectLocation;
        tempSubjectModelLocations[this.locationIndexList[0]].children[this.locationIndexList[1]]
          .children[this.locationIndexList[2]].isDataPlate = this.toggle.checked;
      }
    }
    this.updateSubjectModel();
  }
  getPlaceholder(label: string): string {
    if (label === null || label === undefined) {
      label = '';
    }
    return label;
  }
}

@Component({
    selector: 'app-settings-subject-model-image-upload-dialog',
    templateUrl: './settings-subject-model-image-upload-dialog.component.html',
    styleUrls: ['./settings-subject-model-image-upload-dialog.component.scss'],
    standalone: false
})
export class SettingsSubjectModelImageUploadDialogComponent implements OnInit, OnDestroy {
  subjectModel: SubjectModel;
  uploadImagePath: string;
  uploadFile: File;
  isTrans = false;
  private onDestroy$ = new RxjsSubject();

  @ViewChild('uploadbuttoninput') fileInput: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<SettingsSubjectModelImageUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private snackBar: CommonSnackBarService,
    private dialog: MatDialog,
    private subjectModelService: SubjectModelService,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private networkStatusService: NetworkStatusService,
  ) {
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnInit() {
    this.setEditModelValue();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }
  setEditModelValue() {
    this.subjectModelService.getSubjectModel(this.data.modelId)
      .subscribe(
        model => {
          this.subjectModel = model;
        },
        error => {
          this.snackBar.open(this.translate.instant('settingsSubjectModelImageUploadDialog.setEditModelValueError'), null, null, 5000);
          console.log(error);
        },
        () => {
        });
  }

  public openFileManager() {
    this.networkStatusService.useApplicationFileManager() ? this.showFileManager() : this.fileInput.nativeElement.click();
  }

  private showFileManager() {
    const fileManagerConfig: MatDialogConfig<FileManagerConfig> = {
      disableClose: true,
      ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
      data: {
        operationMode: OperationMode.Import,
        selectionMode: SelectionMode.SingleFile,
        extensions: [IMAGE_FILETYPE.EXTENSION_JPEG, IMAGE_FILETYPE.EXTENSION_PNG, IMAGE_FILETYPE.EXTENSION_JPG],
        handleImportedSelection: this.uploadImage.bind(this),
      },
    };

    this.dialog.open(FileManagerComponent, fileManagerConfig);
  }

  uploadImage(files: File[]) {
    if (files[0]) {
      this.uploadFile = files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadImagePath = <string>reader.result;
      };
      reader.readAsDataURL(files[0]);
    }
  }
  submit() {
    this.oscSpinnerService.show(true);
    this.isTrans = true;
    if (this.uploadFile) {
      this.subjectModelService.updateSubjectModelImage(this.subjectModel.id, this.uploadFile).subscribe(
        res => { },
        error => {
          this.isTrans = false;
          this.snackBar.open(this.translate.instant('settingsSubjectModelImageUploadDialog.submitError'), null, null, 5000);
          console.log(error);
        },
        () => {
          this.oscSpinnerService.hide(true);
          this.isTrans = false;
          this.snackBar.open(this.translate.instant('settingsSubjectModelImageUploadDialog.submitCompleted'), null, null, 5000);
          this.dialogRef.close(this.subjectModel.id);
        }
      );
    } else {
      this.oscSpinnerService.hide(true);
      this.isTrans = false;
      this.snackBar.open(this.translate.instant('settingsSubjectModelImageUploadDialog.submitCompleted'), null, null, 5000);
      this.dialogRef.close(this.subjectModel.id);
    }
  }
}

@Component({
    selector: 'app-settings-subject',
    templateUrl: './settings-subject.component.html',
    styleUrls: ['./settings-subject.component.scss'],
    standalone: false
})
export class SettingsSubjectComponent implements OnInit, OnDestroy {
  @ViewChild('importbutton') importTemplateButton: ElementRef;
  private onDestroy$ = new RxjsSubject();
  title: string;
  serialNumberLabel = TenantConfig.DefaultConfigurationValues.LabelOfSerialNumber;
  browserName: string;
  importFiles: File;
  selectedLocation: SubjectLocation;
  tabIndex = 0;
  serialFound = false;

  isAdmin = false;
  isSubjectImageOpen = true;
  retentionLocation: SubjectLocation = null;
  isCopy: boolean = null;

  modelDataSource: MatTableDataSource<SubjectModelListItem> = new MatTableDataSource();
  subjectDataSource: MatTableDataSource<Subject> = new MatTableDataSource();
  structureDataSource: MatTableDataSource<LocationWrapper> = new MatTableDataSource();
  folderNameDataSource: MatTableDataSource<LocationWrapper> = new MatTableDataSource();
  selectedModel: SubjectModel;
  structure: LocationWrapper[];
  isOnPremiseMode = false;

  nestedTreeControl: NestedTreeControl<SubjectLocation>;
  nestedDataSource: MatTreeNestedDataSource<SubjectLocation>;
  hasNestedChild = (_: number, subjectLocationData: SubjectLocation) =>
    subjectLocationData.children && subjectLocationData.children.length > 0;
  private _getChildren = (node: SubjectLocation) => node.children;

  public get isOffline(): boolean {
    return this.networkStatusService.isOffline;
  }

  get callToAction()  {
    return this.isOffline ? {
      label: 'settingsSubject.import',
      action: () => {
        if (this.useApplicationFileManager) {
          this.showFileManager();
        } else {
          this.importTemplateButton.nativeElement.click();
        }
      }
    }: {
      label: 'commonHeadnavi.newInspectionTemplate',
      action: () => this.openAddModelDialog()
    };
  }

  get useApplicationFileManager(): boolean {
    return this.networkStatusService.useApplicationFileManager();
  }

  constructor(
    private subjectService: SubjectService,
    private subjectModelService: SubjectModelService,
    private structureService: StructureService,
    private utilsService: UtilsService,
    private loginUserService: LoginUserOnlineService,
    private dialog: MatDialog,
    private snackBar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private tenantConfigService: TenantConfigService,
    private storageService: StorageService,
    private networkStatusService: NetworkStatusService,
    private fileSystemAccessService: FileSystemAccessService,
    private subjectModelDbService: SubjectModelDbService
  ) {
    this.langService.prepare(translate, this.onDestroy$, (res) => {
      this.title = this.translate.instant('settingsSubject.title');
    });
  }

  ngOnInit() {
    this.oscSpinnerService.show();
    this.initIsOnPremiseMode();
    this.setConfig();
    this.browserName = this.utilsService.getBrowserName();
    this.isAdmin = this.loginUserService.isAdminRole();
    this.updateSubjectModelList();
    this.nestedTreeControl = new NestedTreeControl<SubjectLocation>(this._getChildren);
    this.nestedDataSource = new MatTreeNestedDataSource();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  initIsOnPremiseMode() {
    this.networkStatusService.isOnPremise$.pipe(takeUntil(this.onDestroy$)).subscribe(isOnPremise => {
      this.isOnPremiseMode = isOnPremise;
    });
  }

  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.serialNumberLabel = configs.find(_ => _.key === TenantConfig.LabelOfSerialNumber.KEY).value as string;
    }
  }

  updateSubjectModelList(): void {
    this.subjectModelService.getSubjectModels(false)
      .subscribe(val => this.setSubjectModelList(val));
  }

  setSubjectModelList(subjectModelList: SubjectModelListItem[]) {
    subjectModelList.sort((subjectModel1, subjectModel2) => {
      if (subjectModel1.name < subjectModel2.name) { return -1; }
      if (subjectModel1.name > subjectModel2.name) { return 1; }
      return 0;
    });
    this.modelDataSource.data = subjectModelList;
    this.oscSpinnerService.hide();
  }

  selectModel(subjectModelId: string) {
    if (!subjectModelId) {
      throw new ApplicationError('Inspection Template is invalid.');
    }

    this.oscSpinnerService.show(true);

    this.subjectModelService.getSubjectModel(subjectModelId)
      .pipe(
        mergeMap(subjectModel => {
          if (!this.isOffline) {
            return of(subjectModel);
          }

          if (!subjectModel.structImagePath) {
            return of(subjectModel);
          }

          const result: RxjsSubject<SubjectModel> = new RxjsSubject<SubjectModel>();
          defer(() =>
            this.fileSystemAccessService.getFileFullPath(subjectModel.structImagePath.replace(/\//g, '\\'))
              .then(x => FILE_SYSTEM.PATH.FILE_PREFIX + x + this.genCacheParam())
              .catch(e => {
                console.log(e);
                return '';
              })
          )
            .subscribe(value => {
              subjectModel.structImagePath = value;
              result.next(subjectModel);
              result.complete();
            });

          return result;
        })
      )
      .subscribe(
        model => {
          if (model) {
            this.selectedModel = model;
            this.nestedDataSource.data = this.selectedModel.locations;
            this.nestedTreeControl.dataNodes = this.selectedModel.locations;
            this.initInspectionTemplateImageOpen();

            // サブジェクトの一覧を取得して、テーブルにセットする。
            this.subjectService.getSubjects(model.id, false).subscribe(val => {
              this.loadSubject(val);
            });

            // 構造のデータを組み立てて、テーブルにセットする。
            this.structure = this.buildStructure(model.locations);
            this.setFolderName();
            this.structureDataSource.data = this.structure;
            this.folderNameDataSource.data = this.structure.filter(location => !location.hasChildren);
          } else {
            this.selectedModel = null;
            this.nestedDataSource.data = null;
            this.nestedTreeControl.dataNodes = null;
            this.subjectDataSource.data = [];
            this.structure = null;
            this.structureDataSource.data = [];
            this.folderNameDataSource.data = [];
          }
          this.oscSpinnerService.hide(true);
        },
        error => {
          console.log(error);
          this.oscSpinnerService.hide(true);
        });
    this.selectedLocation = null;
    this.retentionLocation = null;
    this.isCopy = null;
  }

  initInspectionTemplateImageOpen() {
    const param = this.storageService.fetchSettingSubjectQueryParam(SETTINGS_SUBJECT___QUERY_PARAMS);
    if (param) {
      this.isSubjectImageOpen = param.subjectImageOpen;
    } else {
      this.isSubjectImageOpen = true;
    }
  }

  isSelectedModel(subjectModelId: string) {
    if (!this.selectedModel || !this.selectedModel.id || !subjectModelId) {
      return false;
    }
    return this.selectedModel.id === subjectModelId;
  }

  loadSubject(subjects: Subject[]) {
    this.subjectDataSource.data = subjects.sort((subject1, subject2) => {
      if (subject1.isValid) {
        if (subject2.isValid) {
          if (subject1.serialNo < subject2.serialNo) { return -1; }
          if (subject1.serialNo > subject2.serialNo) { return 1; }
        } else {
          return -1;
        }
      } else {
        if (subject2.isValid) {
          return 1;
        } else {
          if (subject1.serialNo < subject2.serialNo) { return -1; }
          if (subject1.serialNo > subject2.serialNo) { return 1; }
        }
      }
      return 0;
    });
    this.serialFound = this.subjectDataSource.data.length >= 1;
  }

  buildStructure(locations: SubjectLocation[], structure?: LocationWrapper[], nest?: number): LocationWrapper[] {
    if (!structure) {
      structure = [];
    }
    if (!nest) {
      nest = 0;
    }
    locations.forEach(location => {
      const hasChildren = location.children.length > 0;
      structure.push(
        { location: location, nest: nest, hasChildren: hasChildren }
      );
      this.buildStructure(location.children, structure, nest + 1);
    });
    return structure;
  }

  /**
   * フォルダー名を設定する。 (Backend の SubjectModelMapper.SetFolderNameOfLocations を参考に実装。)
   *
   * @returns
   */
  private setFolderName(): void {
    if (!this.isOffline || !this.structure) {
      return;
    }

    let folderIndex: number = 1;
    this.structure.forEach(x => {
      if (x.hasChildren) {
        return;
      }

      let prefix: number = 0;
      if (!x.location.isDataPlate) {
        prefix = folderIndex;
        folderIndex++;
      }

      x.location.folderName = `${(prefix).toString().padStart(2, '0')}_${x.location.identifier}`;
    });
  }

  openDeleteSubjectModelDialog(subjectModel: SubjectModel) {

    this.openDeleteDialog([subjectModel.name], this.translate.instant('settingsSubject.subjectModel'))
      .subscribe(result => {
        if (result) {
          this.deleteSubjectModel(subjectModel.id);
        }
      });
  }

  async openDeleteNotSelectedSubjectModelDialog(id: string) {

    const subjectModel = await this.getSubjectModel(id);
    this.subjectModelDbService.hasInspections(subjectModel.id).subscribe(hasInspections => {

      if (hasInspections) {
        const firstMessage= this.translate.instant('settingsSubject.openErrorMessageDeleteUsedInspectionTemplate');
        this.openErrorMessageDialog(firstMessage, '', '450px', '374px' );
        return;
      }
      this.openDeleteDialog([subjectModel.name], this.translate.instant('settingsSubject.subjectModel'))
        .subscribe(result => {
          if (result) {
            this.deleteSubjectModel(subjectModel.id);
          }
        });
    });
  }

  private openDeleteDialog(title: string[], targetItem: string): Observable<any> {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { target: targetItem, deleteNames: title }
    });
    return dialogRef.afterClosed();
  }

  openLocationDeleteDialog(isPaste: boolean, location: SubjectLocation, isUp: boolean, isDelete: boolean) {
    const dialogRef = this.dialog.open(CommonDeleteDialogComponent, {
      width: '600px',
      height: '300px',
      maxHeight: '800px',
      data: { target: this.translate.instant('settingsSubject.Location'), deleteNames: [location.name] }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.validateIsUpdate(isPaste, location, isUp, isDelete);
      }
    });
  }

  private deleteSubjectModel(id: string): void {
    this.oscSpinnerService.show();

    this.subjectModelService.deleteSubjectModel(id).subscribe(
      val => { },
      error => {
        this.oscSpinnerService.hide();
        this.snackBar.open(this.translate.instant('settingsSubject.deleteSubjectModelFailed'));
        console.log(error);
      },
      () => {
        this.updateSubjectModelList();
        this.selectedModel = null;
      }
    );
  }

  openDeleteSubject(subject: Subject) {
    this.openDeleteDialog([subject.serialNo], this.translate.instant('settingsSubject.subject'))
      .subscribe(result => {
        if (result) {
          this.deleteSubject(subject.id);
        }
      });
  }

  private deleteSubject(id: string): void {
    this.oscSpinnerService.show();

    this.subjectService.deleteSubject(id).subscribe(
      val => { },
      error => {
        this.oscSpinnerService.hide();
        this.snackBar.open(this.translate.instant('settingsSubject.deleteSubjectFailed'));
        console.log(error);
      },
      () => {
        // サブジェクトの一覧を取得して、テーブルにセットする。
        this.subjectService.getSubjects(this.selectedModel.id, false).subscribe(val => {
          this.loadSubject(val);
          this.oscSpinnerService.hide();
        });
      }
    );
  }

  updateSubject(subject: Subject) {
    if (!subject || !subject.id) {
      throw new ApplicationError('Serial Number is invalid.');
    }

    // Re-obtain the Subject and obtain the flag indicating whether the latest Attributes have been used for inspection or not
    this.oscSpinnerService.show();
    this.subjectService.getSubject(subject.id)
      .pipe(
        mergeMap(subject => {
          this.oscSpinnerService.hide();
          return this.openEditSubjectDialog(this.selectedModel.id, subject);
        }),
        mergeMap((_) => this.subjectService.getSubjects(this.selectedModel.id, false)),
        map(subjects => this.loadSubject(subjects)),
      ).subscribe(
        (_) => _,
        err => {
          console.log(err);
          this.oscSpinnerService.hide();
          this.snackBar.open(this.translate.instant('settingsSubject.getSubjectFailed', { serialNumberLabel: this.serialNumberLabel }));
        }
      );
  }

  insertSubject() {
    this.openEditSubjectDialog(this.selectedModel.id).subscribe(
      result => this.subjectService.getSubjects(this.selectedModel.id, false).subscribe(val => this.loadSubject(val)));
  }

  openEditSubjectDialog(subjectModelId: string, subject?: Subject): Observable<Subject> {
    const dialogRef = this.dialog.open(SettingsSubjectEditDialogComponent, {
      width: '625px',
      maxHeight: '95%',
      autoFocus: false,
      data: { subjectModelId: subjectModelId, subject: subject }
    });

    const elements = document.getElementsByClassName('cdk-overlay-pane');
    Array.from(elements).forEach((item: HTMLElement) => {
      item.style.overflowY = 'auto';
    });

    // Clean up on dialog close
    dialogRef.afterClosed().subscribe(() => {
      document.body.style.paddingRight = '';
      document.body.style.overflow = '';
    });

    return dialogRef.afterClosed();
  }

  downloadSubject() {
    this.structureService.getStructureZip(this.selectedModel.id, null, false)
      .subscribe(
        blob => {
          this.utilsService.download(blob, this.selectedModel.name + '.zip', null);
        },
        error => {
          this.snackBar.open(this.translate.instant('settingsSubject.downloadSubjectFailed'));
          console.log(error);
        });
  }

  importSubjectModel(files: File[]): void {
    if (!files || files.length !== 1) {
      throw new ApplicationError('Files are invalid.');
    }

    const file = files[0];
    this.importFiles = null;

    const dialogRef = this.openImportConfirmDialog(file.name);
    dialogRef.afterClosed()
      .subscribe(result => {
        if (result) {
          this.oscSpinnerService.show();
          this.subjectModelService.importSubjectModel(file).subscribe(
            value => { },
            (error: HttpErrorResponse) => {
              this.oscSpinnerService.hide();
              if (error.status === 409 || error.status === 400) {
                let firstMessage  ='';
                let secondMessage  ='';
                let height ='';

                const width ='374px';
                if (error.status === 409) {
                  firstMessage = this.translate.instant('settingsSubject.openErrorMessageDialog409FirstMessage');
                  secondMessage = this.translate.instant('settingsSubject.openErrorMessageDialog409SecondMessage');
                  height ='480px';
                }
                else if (error.status === 400) {
                  firstMessage = this.translate.instant('settingsSubject.openErrorMessageDialog400FirstMessage');
                  secondMessage = this.translate.instant('settingsSubject.openErrorMessageDialog400SecondMessage');
                  height ='450px';
                }
                this.openErrorMessageDialog( firstMessage, secondMessage, height, width);
              } else {
                this.snackBar.open(this.translate.instant('settingsSubject.importSubjectModelFailed'));
                console.log(error);
              }
            },
            () => {
              this.updateSubjectModelList();
              this.snackBar.open(this.translate.instant('settingsSubject.importSubjectModelSuccessfully'));
            }
          );
        }
      });
  }

  exportSubjectModel() {
    this.subjectModelService.exportSubjectModel(this.selectedModel.id)
      .subscribe(
        blob => this.utilsService.download(blob, this.selectedModel.name + '.zip', null),
        error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('settingsSubject.exportSubjectModelFailed'));
        },
        () => this.snackBar.open(this.translate.instant('settingsSubject.exportSubjectModelSuccessfully')));
  }
  selectLocation4FilterImages(location: SubjectLocation) {
    this.selectedLocation = location;
  }

  openImportConfirmDialog(filename: string): MatDialogRef<CommonImportConfirmDialogComponent, any> {
    return this.dialog.open(CommonImportConfirmDialogComponent, {
      width: CommonImportConfirmDialogComponent.defaultDialogWidth,
      height: CommonImportConfirmDialogComponent.defaultDialogHeight,
      data: { fileName: filename }
    });
  }

  openAddModelDialog() {
    const dialogRef = this.dialog.open(SubjectModelEditDialogComponent, {
      minWidth: '600px',
      minHeight: '366px',
      autoFocus: false,
      data: { flag: 'new', modelId: null }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateSubjectModelList();
        this.selectModel(result);
        // this.tabIndex = 1;
      }
    });
  }
  openEditModelDialog() {
    const dialogRef = this.dialog.open(SubjectModelEditDialogComponent, {
      minWidth: '600px',
      minHeight: '366px',
      autoFocus: false,
      data: { flag: 'edit', modelId: this.selectedModel.id }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateSubjectModelList();
        this.selectModel(result);
      }
    });
  }
  openCloneModelDialog() {
    const dialogRef = this.dialog.open(SubjectModelEditDialogComponent, {
      minWidth: '600px',
      minHeight: '300px',
      autoFocus: false,
      data: { flag: 'clone', modelId: this.selectedModel.id }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateSubjectModelList();
        this.selectModel(result);
      }
    });
  }
  openImageUploadDialog() {
    const dialogRef = this.dialog.open(SettingsSubjectModelImageUploadDialogComponent, {
      width: '600px',
      height: '500px',
      autoFocus: false,
      data: { flag: 'edit', modelId: this.selectedModel.id }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateSubjectModelList();
        this.selectModel(result);
        this.tabIndex = 1;
      }
    });
  }
  openEditLocationDialog(isEdit: boolean, subjectLocation: SubjectLocation) {
    const indexListFrom: number[] = subjectLocation ? this.getLocationIndex(subjectLocation.identifier) : [];
    const dialogRef = this.dialog.open(SettingsSubjectLocationEditDialogComponent, {
      width: '800px',
      maxHeight: '95%',
      autoFocus: false,
      data: {
        isEdit: isEdit, model: this.selectedModel, subjectLocation: subjectLocation,
        indexList: indexListFrom
      }
    });

    const elements = document.getElementsByClassName('cdk-overlay-pane');
    Array.from(elements).forEach((item: HTMLElement) => {
      item.style.overflowY = 'auto';
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateSubjectModelList();
        this.selectedModel = result;
        this.nestedDataSource.data = this.selectedModel.locations;
        this.nestedTreeControl.dataNodes = this.selectedModel.locations;
        // 選択中のロケーションのツリー表示を開く
        if (indexListFrom.length > 1 || (indexListFrom.length === 1 && !isEdit)) {
          this.nestedTreeControl.expand(this.nestedTreeControl.dataNodes[indexListFrom[0]]);
        }
        if (indexListFrom.length > 2 || (indexListFrom.length === 2 && !isEdit)) {
          this.nestedTreeControl.expand(this.nestedTreeControl.dataNodes[indexListFrom[0]].children[indexListFrom[1]]);
        }
        // 構造のデータを組み立てて、テーブルにセットする。
        this.structure = this.buildStructure(this.selectedModel.locations);
        this.setFolderName();
        this.folderNameDataSource.data = this.structure.filter(location => !location.hasChildren);
        this.selectedLocation = null;
        this.retentionLocation = null;
        this.isCopy = null;
      }
    });
  }

  // 終端ロケーションが存在する最上位ロケーションを取得
  getPrimaryLocation(termLocIdentifier: string): SubjectLocation {
    let primeLoc: SubjectLocation;
    this.selectedModel.locations.forEach(
      loc => {
        if (this.isIncludeTermLoc(termLocIdentifier, loc)) {
          primeLoc = loc;
        }
      });
    return primeLoc;
  }
  private isIncludeTermLoc(termLocIdentifier: string, targetLoc: SubjectLocation): boolean {
    if (targetLoc.identifier === termLocIdentifier) {
      return true;
    } else {
      let result: boolean;
      for (const child of targetLoc.children) {
        result = this.isIncludeTermLoc(termLocIdentifier, child);
        if (result) {
          break;
        }
      }
      return result;
    }
  }
  isSafari(browserName): boolean {
    return this.utilsService.isSafari(browserName);
  }
  getLocationIndex(termLocIdentifier: string): number[] {
    const indexList: number[] = [];
    this.selectedModel.locations.some(
      (firstLoc, firstIndex) => {
        if (firstLoc.identifier === termLocIdentifier) {
          indexList.push(firstIndex);
          return true;
        }
        if (firstLoc.children.some(
          (secondLoc, secondIndex) => {
            if (secondLoc.identifier === termLocIdentifier) {
              indexList.push(firstIndex, secondIndex);
              return true;
            }
            if (secondLoc.children.some(
              (thirdLoc, thirdIndex) => {
                if (thirdLoc.identifier === termLocIdentifier) {
                  indexList.push(firstIndex, secondIndex, thirdIndex);
                  return true;
                }
              })) {
              return true;
            }
          })) {
          return true;
        }
      });
    return indexList;
  }
  getLocationDeep(): number {
    if (!this.retentionLocation.children || this.retentionLocation.children.length === 0) {
      return 1;
    } else if (this.retentionLocation.children.filter(secondLoc => secondLoc.children && secondLoc.children.length > 0).length === 0) {
      return 2;
    } else {
      return 3;
    }
  }
  isContainLocation(termLocIdentifier: string): boolean {
    return this.retentionLocation.identifier === termLocIdentifier
      || this.retentionLocation.children.filter(loc => loc.identifier === termLocIdentifier).length > 0;
  }

  upDownLocationUpdate(isUp: boolean) {
    const indexList = this.getLocationIndex(this.selectedLocation.identifier);
    const tempSubjectModel = this.selectedModel;
    let tempLocation: SubjectLocation;
    if (indexList.length === 1) {
      // 第一階層ロケーションの移動
      if (isUp) {
        tempLocation = tempSubjectModel.locations[indexList[0]];
        tempSubjectModel.locations[indexList[0]] = tempSubjectModel.locations[indexList[0] - 1];
        tempSubjectModel.locations[indexList[0] - 1] = tempLocation;
      } else {
        tempLocation = tempSubjectModel.locations[indexList[0]];
        tempSubjectModel.locations[indexList[0]] = tempSubjectModel.locations[indexList[0] + 1];
        tempSubjectModel.locations[indexList[0] + 1] = tempLocation;
      }
    } else if (indexList.length === 2) {
      // 第二階層ロケーションの移動
      if (isUp) {
        tempLocation = tempSubjectModel.locations[indexList[0]].children[indexList[1]];
        tempSubjectModel.locations[indexList[0]].children[indexList[1]] =
          tempSubjectModel.locations[indexList[0]].children[indexList[1] - 1];
        tempSubjectModel.locations[indexList[0]].children[indexList[1] - 1] = tempLocation;
      } else {
        tempLocation = tempSubjectModel.locations[indexList[0]].children[indexList[1]];
        tempSubjectModel.locations[indexList[0]].children[indexList[1]] =
          tempSubjectModel.locations[indexList[0]].children[indexList[1] + 1];
        tempSubjectModel.locations[indexList[0]].children[indexList[1] + 1] = tempLocation;
      }
    } else if (indexList.length === 3) {
      // 第三階層ロケーションの移動
      if (isUp) {
        tempLocation = tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2]];
        tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2]] =
          tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2] - 1];
        tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2] - 1] = tempLocation;
      } else {
        tempLocation = tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2]];
        tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2]] =
          tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2] + 1];
        tempSubjectModel.locations[indexList[0]].children[indexList[1]].children[indexList[2] + 1] = tempLocation;
      }
    }
    this.updateSubjectModel(tempSubjectModel, this.selectedLocation.identifier);
  }

  pasteLocationUpdate(toLocation: SubjectLocation) {
    const indexListFrom: number[] = this.getLocationIndex(this.retentionLocation.identifier);
    let indexListTo: number[];
    if (toLocation) {
      indexListTo = this.getLocationIndex(toLocation.identifier);
    } else {
      indexListTo = [];
    }

    if (!this.isDuplication(this.selectedModel, toLocation, indexListTo) && !this.isDataPlateFirstLocation(this.selectedModel, this.retentionLocation, indexListTo)) {
      // abbreviationの重複がない場合
      this.updateSubjectModel(this.getTmpLocation(indexListFrom, indexListTo), this.retentionLocation.identifier);
    } else if (this.isDataPlateFirstLocation(this.selectedModel, this.retentionLocation, indexListTo)) {
      // 自分自身がデータプレートで、移動先が第一階層でかつ、dataplateが存在したらエラー
      this.snackBar.open(this.translate.instant('settingsSubject.moveLocationUpdateErrorForDataplate'), null, null, 5000);
    } else if (this.isDuplication(this.selectedModel, toLocation, indexListTo)) {
      // abbreviationが重複した場合はabbreviationをReNameして張り付ける
      const dialogRef = this.dialog.open(LocationAbbreviationEditDialogComponent, {
        width: '600px',
        height: '440px',
        data: {
          retentionLocation: this.retentionLocation,
          identifierPrefix: toLocation ? toLocation.identifier : '',
          subjectLocationsExceptMine: JSON.parse(JSON.stringify(this.selectedModel.locations)),
          trueBtnText: this.translate.instant('commonConfirmDialog.ok'),
          falseBtnText: this.translate.instant('commonConfirmDialog.cancel'),
          cancellable: true
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.updateSubjectModel(this.getTmpLocation(indexListFrom, indexListTo, result), this.retentionLocation.identifier);
        }
      });
    }
  }

  getTmpLocation(indexListFrom: number[], indexListTo: number[], result: any = null): SubjectModel {
    const tmpSubjectModel = this.selectedModel;
    let tmpLocation: SubjectLocation;

    // コピー元のロケーションを退避
    if (indexListFrom.length === 1) {
      tmpLocation = JSON.parse(JSON.stringify(tmpSubjectModel.locations[indexListFrom[0]]));
    } else if (indexListFrom.length === 2) {
      tmpLocation = JSON.parse(JSON.stringify(tmpSubjectModel.locations[indexListFrom[0]].children[indexListFrom[1]]));
    } else if (indexListFrom.length === 3) {
      tmpLocation = JSON.parse(JSON.stringify(tmpSubjectModel.locations[indexListFrom[0]].children[indexListFrom[1]].children[indexListFrom[2]]));
    }

    // ReNameしたAbbreviation適用
    if (result) {
      tmpLocation.name = result.name;
      tmpLocation.abbreviation = result.abbreviation;
    }

    // コピー先にロケーション追加
    if (indexListTo.length === 0) {
      tmpLocation.identifier = tmpLocation.abbreviation;
      if (tmpLocation.rectInStructImage.top === null) {
        tmpLocation.rectInStructImage.top = 0; // 初期値:0
        tmpLocation.rectInStructImage.left = 0; // 初期値:0
        tmpLocation.rectInStructImage.width = 0; // 初期値:0
        tmpLocation.rectInStructImage.height = 0; // 初期値:0
      }
      tmpLocation.children.forEach(location1 => {
        location1.identifier = tmpLocation.identifier + '_' + location1.abbreviation;
        location1.children.forEach(location2 => {
          location2.identifier = location1.identifier + '_' + location2.abbreviation;
        });
      });
      tmpSubjectModel.locations.push(tmpLocation);
    } else if (indexListTo.length === 1) {
      tmpLocation.identifier = tmpSubjectModel.locations[indexListTo[0]].identifier + '_' + tmpLocation.abbreviation;
      tmpLocation.isDataPlate = tmpSubjectModel.locations[indexListTo[0]].isDataPlate;
      tmpLocation.rectInStructImage = new RectInStructImage();
      tmpLocation.children.forEach(location => {
        location.identifier = tmpLocation.identifier + '_' + location.abbreviation;
        location.isDataPlate = tmpLocation.isDataPlate;
      });
      tmpSubjectModel.locations[indexListTo[0]].children.push(tmpLocation);
    } else if (indexListTo.length === 2) {
      tmpLocation.identifier = tmpSubjectModel.locations[indexListTo[0]].children[indexListTo[1]].identifier
        + '_' + tmpLocation.abbreviation;
      tmpLocation.rectInStructImage = new RectInStructImage();
      tmpLocation.isDataPlate = tmpSubjectModel.locations[indexListTo[0]].isDataPlate;
      tmpSubjectModel.locations[indexListTo[0]].children[indexListTo[1]].children.push(tmpLocation);
    }

    if (!this.isCopy) {
      // 移動元からロケーション削除
      if (indexListFrom.length === 1) {
        tmpSubjectModel.locations.splice(indexListFrom[0], 1);
      } else if (indexListFrom.length === 2) {
        tmpSubjectModel.locations[indexListFrom[0]].children.splice(indexListFrom[1], 1);
      } else if (indexListFrom.length === 3) {
        tmpSubjectModel.locations[indexListFrom[0]].children[indexListFrom[1]].children.splice(indexListFrom[2], 1);
      }
    }

    return tmpSubjectModel;
  }

  isRetention(subLocation: any): boolean {
    return !this.isCopy
      && subLocation
      && this.retentionLocation
      && (subLocation.identifier === this.retentionLocation.identifier
        || this.retentionLocation.children.some(child1 => child1.identifier === subLocation.identifier)
        || this.retentionLocation.children.some(child1 => child1.children.some(child2 => child2.identifier === subLocation.identifier)));
  }

  updateSubjectModel(subjectModel: SubjectModel, identifier: string) {
    const subjectModelUpdate = new SubjectModelUpdate();
    subjectModelUpdate.name = subjectModel.name;
    subjectModelUpdate.locations = subjectModel.locations;
    subjectModelUpdate.schemaVersion = subjectModel.schemaVersion;
    subjectModelUpdate.id = subjectModel.id;
    subjectModelUpdate.isValid = subjectModel.isValid;
    subjectModelUpdate.inspectionType = subjectModel.inspectionType;

    this.oscSpinnerService.show();
    this.subjectModelService.updateSubjectModel(subjectModelUpdate).subscribe(
      res => {
        this.selectedModel = res;
        this.nestedDataSource.data = this.selectedModel.locations;
        this.nestedTreeControl.dataNodes = this.selectedModel.locations;
        // 選択中のロケーションのツリー表示を開く
        const indexListFrom: number[] = this.getLocationIndex(identifier);
        if (identifier && indexListFrom.length > 1) {
          this.nestedTreeControl.expand(this.selectedModel.locations[indexListFrom[0]]);
        }
        if (identifier && indexListFrom.length > 2) {
          this.nestedTreeControl.expand(this.selectedModel.locations[indexListFrom[0]].children[indexListFrom[1]]);
        }
        // 構造のデータを組み立てて、テーブルにセットする。
        this.structure = this.buildStructure(this.selectedModel.locations);
        this.setFolderName();
        this.folderNameDataSource.data = this.structure.filter(location => !location.hasChildren);
      },
      error => {
        this.snackBar.open(this.translate.instant('settingsSubject.updateSubjectModelError'), null, null, 5000);
        this.oscSpinnerService.hide();
        console.log(error);
      },
      () => {
        this.retentionLocation = null;
        this.isCopy = null;
        this.updateSubjectModelList();
        this.snackBar.open(this.translate.instant('settingsSubject.updateSubjectModelCompleted'), null, null, 5000);
      }
    );
  }

  // Copy/CutしたLocation情報の保持
  locationRetention(location: SubjectLocation, isCopy: boolean) {
    this.retentionLocation = location;
    this.isCopy = isCopy;
  }

  // Pasteボタンの非活性条件
  isDisablePaste(subjectLocation: SubjectLocation): boolean {
    if (this.isOffline) {
      return true;
    }

    return !this.retentionLocation
      || this.retentionLocation === subjectLocation
      || this.isContainLocation(subjectLocation.identifier)
      || this.getLocationDeep() + this.getLocationIndex(subjectLocation.identifier).length >= 4;
  }

  getPasteTooltipText(): string {
    return this.retentionLocation ? this.translate.instant('settingsSubject.pasteLocation') + ' ' + this.retentionLocation.name : null;
  }

  validateIsUpdate(isPaste: boolean, location: SubjectLocation, isUp: boolean, isDelete: boolean) {
    if (this.selectedModel.hasInspections) {
      const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
        width: '500px',
        height: '268px',
        data: {
          firstMessage: this.translate.instant('settingsSubjectLocationEditDialog.confirmText1'),
          secondMessage: this.translate.instant('settingsSubjectLocationEditDialog.confirmText2'),
          trueBtnText: this.translate.instant('commonConfirmDialog.ok'),
          falseBtnText: this.translate.instant('commonConfirmDialog.cancel'),
          dialogType: 'confirmation'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          if (isPaste) {
            this.pasteLocationUpdate(location);
          } else if (isDelete) {
            this.deleteLocation(location);
          } else {
            this.upDownLocationUpdate(isUp);
          }
        }
      });
    } else {
      if (isPaste) {
        this.pasteLocationUpdate(location);
      } else if (isDelete) {
        this.deleteLocation(location);
      } else {
        this.upDownLocationUpdate(isUp);
      }
    }
  }

  isRectInStructImageAllZero(rectInStructImage: RectInStructImage): boolean {
    return rectInStructImage.top === 0 && rectInStructImage.left === 0 && rectInStructImage.width === 0 && rectInStructImage.height === 0;
  }
  changeStringCase(value: string): string {
    return value.toLowerCase();
  }
  isDataPlateFirstLocation(subjectModel: SubjectModel, selectedLocation: SubjectLocation, indexListTo: number[]): boolean {
    let isDataPlateFirstLocation = false;
    const subjectLocationsExceptMine = JSON.parse(JSON.stringify(subjectModel.locations));
    if (indexListTo.length === 0) {
      subjectLocationsExceptMine.forEach(location => {
        if (selectedLocation.isDataPlate && location.isDataPlate) {
          isDataPlateFirstLocation = true;
        }
      });
    }
    return isDataPlateFirstLocation;
  }
  isDuplication(selectedSubjectModel: SubjectModel, toSubjectModelLocation: SubjectLocation, indexListTo: number[]): boolean {
    let isDuplication = false;
    const subjectLocationsExceptMine = JSON.parse(JSON.stringify(selectedSubjectModel.locations));
    if (indexListTo.length === 0) {
      subjectLocationsExceptMine.forEach(location => {
        if (this.changeStringCase(location.abbreviation) === this.changeStringCase(this.retentionLocation.abbreviation)) {
          isDuplication = true;
        }
      });
    } else {
      subjectLocationsExceptMine.forEach(location => {
        location.children.forEach(secondLoc => {
          if (location.identifier === toSubjectModelLocation.identifier) {
            if (this.changeStringCase(secondLoc.abbreviation) === this.changeStringCase(this.retentionLocation.abbreviation)) {
              isDuplication = true;
            }
          }
          secondLoc.children.forEach(thirdLoc => {
            if (secondLoc.identifier === toSubjectModelLocation.identifier) {
              if (this.changeStringCase(thirdLoc.abbreviation) === this.changeStringCase(this.retentionLocation.abbreviation)) {
                isDuplication = true;
              }
            }
          });
        });
      });
    }
    return isDuplication;
  }

  openErrorMessageDialog(firstMessage : string, secondMessage: string, height: string, width:string): void {

    this.dialog.open(CommonErrorMessageDialogComponent, {
      width: width,
      height: height,
      data: {
        firstMessage: firstMessage,
        secondMessage: secondMessage
      }
    });
  }

  // 第一階層へのPasteボタンの非活性条件
  isDisabledPasteTopLocation(): boolean {
    if (this.isOffline) {
      return true;
    }
    if (this.isCopy) {
      return !this.retentionLocation;
    } else {
      return !this.retentionLocation
        || (this.retentionLocation.rectInStructImage.height !== null
          && this.retentionLocation.rectInStructImage.width !== null
          && this.retentionLocation.rectInStructImage.top !== null
          && this.retentionLocation.rectInStructImage.left !== null);
    }
  }

  clickSubjectImageHide() {
    this.isSubjectImageOpen = !this.isSubjectImageOpen;
    let param: SettingSubjectQueryParam = this.storageService.fetchSettingSubjectQueryParam(SETTINGS_SUBJECT___QUERY_PARAMS);
    if (!param) {
      param = {
        subjectImageOpen: this.isSubjectImageOpen,
      };
    } else { param.subjectImageOpen = this.isSubjectImageOpen; }
    this.storageService.pushSettingSubjectQueryParam(SETTINGS_SUBJECT___QUERY_PARAMS, param);
  }

  deleteLocation(location: SubjectLocation) {
    const indexListFrom: number[] = this.getLocationIndex(location.identifier);
    const tmpSubjectModel = this.selectedModel;

    // 移動元からロケーション削除
    if (indexListFrom.length === 1) {
      tmpSubjectModel.locations.splice(indexListFrom[0], 1);
    } else if (indexListFrom.length === 2) {
      tmpSubjectModel.locations[indexListFrom[0]].children.splice(indexListFrom[1], 1);
    } else if (indexListFrom.length === 3) {
      tmpSubjectModel.locations[indexListFrom[0]].children[indexListFrom[1]].children.splice(indexListFrom[2], 1);
    }

    this.updateSubjectModel(tmpSubjectModel, null);
  }

  /**
   * キャッシュ用パラーメーターを生成する。
   *
   * @returns パラメーター文字列。
   */
  private genCacheParam(): string {
    return '?var=' + performance.now();
  }

  private async getSubjectModel(id: string): Promise<SubjectModel> {
    this.oscSpinnerService.show();

    try {
      const subjectModel = await firstValueFrom(this.subjectModelService.getSubjectModel(id));
      this.oscSpinnerService.hide();
      return subjectModel;
    } catch (error) {
      this.oscSpinnerService.hide();
      console.log(error);
      throw error;
    }
  }

  showFileManager() {
    const supportedExtensions = {
      [COMPRESSED_FILETYPE.EXTENSION_ZIP]: COMPRESSED_FILETYPE.ZIP,
    };

    const fileManagerConfig: MatDialogConfig<FileManagerConfig> = {
      disableClose: true,
      ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
      data: {
        operationMode: OperationMode.Import,
        selectionMode: SelectionMode.SingleFile,
        extensions: supportedExtensions,
        handleImportedSelection: this.importSubjectModel.bind(this),
      },
    };

    this.dialog.open(FileManagerComponent, fileManagerConfig);
  }

}
